//
// Copyright (c) 2011-2025 by Curalate, Inc.
//

import React, { useState } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import styled from 'styled-components';

import { ProductDetailsModalAlerts } from '@campaigns/components/shared/product-details-modal/modal/ProductDetailsModalAlerts';
import { ProductDetailsTab } from '@campaigns/components/shared/product-details-modal/tabs/ProductDetailsTab';
import { VariantsTab } from '@campaigns/components/shared/product-details-modal/tabs/VariantsTab';
import { ProductDetailsFieldLabels } from '@campaigns/constants/ProductDetailsModalFieldLabels';
import { SAMPLING_CAMPAIGNS_TRACKING_ID } from '@campaigns/constants/sampling/SamplingCampaignsTrackingId';
import { EditableBVSamplingProduct } from '@campaigns/types/sampling/SamplingProductTypes';
import { getBVProductFormErrorMessages } from '@campaigns/utils/FormUtils';
import { Tabs } from '@components/primitives/tabs/Tabs';

interface ProductDetailsModalContentProps {
  isViewOnly: boolean;
}

interface TabItem {
  content: React.ReactNode;
  isDisabled: boolean;
  title: string;
  value: string;
  trackingId: string;
  isHidden?: boolean;
}

export const ProductDetailsModalContent = ({
  isViewOnly,
}: ProductDetailsModalContentProps) => {
  const {
    control,
    formState: { errors },
  } = useFormContext<EditableBVSamplingProduct>();
  const variations = useWatch({ control, name: 'variations' });
  const hasMultipleVariants = variations.length > 1;
  const hideVariantsTab = isViewOnly && !hasMultipleVariants;
  const [currentTab, setCurrentTab] = useState('product-details');

  const errorMessages = getBVProductFormErrorMessages(
    errors,
    ProductDetailsFieldLabels,
  );

  const TABS: TabItem[] = [
    {
      content: <ProductDetailsTab isViewOnly={isViewOnly} />,
      isDisabled: false,
      title: 'Product Details',
      value: 'product-details',
      trackingId:
        SAMPLING_CAMPAIGNS_TRACKING_ID.PRODUCT_MODAL_PRODUCT_DETAILS_TAB,
    },
    {
      content: <VariantsTab isViewOnly={isViewOnly} />,
      isDisabled: false,
      title: 'Variants',
      value: 'variants',
      trackingId: SAMPLING_CAMPAIGNS_TRACKING_ID.PRODUCT_MODAL_VARIANTS_TAB,
      isHidden: hideVariantsTab,
    },
  ];

  // Only show tabs that are not hidden.
  // This allows us to conditionally remove tabs (like "Variants" in view-only mode with a single variant)
  const visibleTabs = TABS.filter((tab) => !tab.isHidden);

  return (
    <>
      <ProductDetailsModalAlerts
        errorMessages={errorMessages}
        isViewOnly={isViewOnly}
      />
      <StyledTabs defaultValue='product-details' onValueChange={setCurrentTab}>
        <Tabs.List>
          {visibleTabs.map((tab) => (
            <Tabs.Trigger
              key={tab.value}
              disabled={tab.isDisabled}
              trackingId={tab.trackingId}
              value={tab.value}
            >
              {tab.title}
            </Tabs.Trigger>
          ))}
        </Tabs.List>
        {visibleTabs.map((tab) => (
          <StyledTabContent
            key={tab.value}
            forceMount
            hidden={tab.value !== currentTab}
            value={tab.value}
          >
            {tab.content}
          </StyledTabContent>
        ))}
      </StyledTabs>
    </>
  );
};

const StyledTabs = styled(Tabs)`
  box-shadow: none;
`;

const StyledTabContent = styled(Tabs.Content)`
  padding-top: ${({ theme }) => theme.spacings[3]};
`;
